/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { IMaskMixin } from 'react-imask';

import TextFieldProps from './type';
import Icon from '../Icon';
import {
  TextFieldMessageStyled,
  TextFieldWrapperStyled,
  TextFieldStyled,
  TextFieldLabelWrapperStyled,
  ContentStyled,
} from './styled';

const MaskedStyledInput = IMaskMixin(({ inputRef, ...props }: any) => (
  <TextFieldStyled {...props} ref={inputRef} />
));

const TextField = ({ ...props }: TextFieldProps) => {
  const {
    definition: definitionProp,
    disabled,
    endAdornment,
    fullWidth,
    helperText: helperTextProp,
    helperType: helperTypeProp,
    label,
    required,
    startAdornment,
    onChange: onChangeProp,
    onFieldClick: onFieldClickProp,
    onKeyDown: onKeyDownProp,
    onlyLabel,
    value: valueProp,
    type,
    readOnly,
    placeholder,
    mask: maskProp,
    maskValue: maskValueProp,
    style,
    ...rest
  } = props;

  const [value, setValue] = useState(valueProp || '');
  const helperText =
    disabled && (helperTextProp === null || helperTextProp === undefined)
      ? 'Este campo no puede editarse'
      : helperTextProp;
  const helperType = disabled && !helperTypeProp ? 'disabled' : helperTypeProp;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChangeProp) {
      onChangeProp(event.target.value);
    }
    setValue(event.target.value);
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (!disabled && onFieldClickProp) onFieldClickProp(event);
  };

  const handleChangeMask = (mask: any, currentValue: string) => {
    const { _unmaskedValue: unmaskedValue } = mask;
    if (onChangeProp) {
      onChangeProp(maskValueProp ? currentValue : unmaskedValue);
    }
    setValue(currentValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (onKeyDownProp) onKeyDownProp(event, event.key);
  };

  const iconProps = () => {
    switch (helperType) {
      case 'error':
        return { name: 'errorOutlined', color: '#F65940' };
      case 'warning':
        return { name: 'warningOutlined', color: '#FBC832' };
      case 'success':
        return { name: 'checkCircleOutlined', color: '#95D31C' };
      default:
        return { name: 'notInterestedOutlined', color: '#5B5D71' };
    }
  };

  useEffect(() => {
    setValue(valueProp || '');
  }, [valueProp]);

  return (
    <TextFieldWrapperStyled
      disabled={disabled}
      fullWidth={fullWidth}
      style={{ ...style }}
      {...rest}
    >
      {label && (
        <TextFieldLabelWrapperStyled disabled={disabled}>
          <p
            style={{
              margin: 0,
              ...(value && !disabled ? { color: '#0057B8' } : {}),
            }}
          >
            {label}
          </p>
          {!onlyLabel && (
            <p
              style={{
                margin: 0,
                ...(value && !disabled ? { color: '#0057B8' } : {}),
              }}
            >
              {required ? 'Obligatorio' : 'Opcional'}
            </p>
          )}
        </TextFieldLabelWrapperStyled>
      )}

      <ContentStyled
        value={value}
        disabled={disabled}
        helperText={helperText}
        helperType={helperType}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
      >
        {startAdornment}
        {maskProp ? (
          <MaskedStyledInput
            disabled={disabled}
            definitions={definitionProp}
            placeholder={placeholder}
            readOnly={readOnly}
            type={type}
            value={value}
            mask={maskProp}
            radix="."
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onAccept={(maskedValue, mask) => handleChangeMask(mask, maskedValue)}
          />
        ) : (
          <TextFieldStyled
            disabled={disabled}
            placeholder={placeholder}
            readOnly={readOnly}
            type={type}
            mask={maskProp}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
          />
        )}
        {endAdornment}
      </ContentStyled>

      {helperText && (
        <TextFieldMessageStyled disabled={disabled} helperType={helperType}>
          <Icon {...iconProps()} />
          {helperText}
        </TextFieldMessageStyled>
      )}
    </TextFieldWrapperStyled>
  );
};

TextField.defaultProps = {
  mask: '',
  maskValue: false,
  onKeyDown: () => {},
};

export default TextField;
